import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text11 from '@sdk/components/ui/typography/Text11';
import Text12 from '@sdk/components/ui/typography/Text12';
import Text14 from '@sdk/components/ui/typography/Text14';
import Picker from '@sdk/components/forms/Picker';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { AutomatedBreakType } from '@sdk/settings/types';

import { AutomatedBreakFormFields } from '@web/settings/components/schedule/Breaks/breaks.types';
import SettingsSwitcherRow from '@web/settings/components/SettingsSwitcherRow';
import AutomationRuleSummary from '@web/settings/components/schedule/Breaks/AutomatedBreak/AutomationRuleSummary';
import { CustomTextField } from '@web/onboarding/Checklist/components/CustomTextField';
import { useAutomatedBreakFields } from '@web/settings/components/schedule/Breaks/AutomatedBreak/automatedBreak.hooks';
import { useAutomatedBreaksPremiumPayFeatureFlagEnabled } from '@web/automatedBreaks/hooks/useAutomatedBreaksPremiumPayFeatureFlagEnabled';
import { BREAK_CATEGORY_OPTIONS } from '@web/settings/components/schedule/Breaks/breaks.consts';
import { FIELD_WIDTH_205, FIELD_WIDTH_74 } from '@web/settings/constants';
import { WaivingSettings } from '@web/settings/components/schedule/Breaks/WaivingSettings/WaivingSettings';
import { useIsAttestationsFeatureFlagEnabled } from '@web/automatedBreaks/hooks/useIsAttestationsFeatureFlagEnabled';
import { CANNOT_BE_MORE_THAN_WAIVER_ERROR } from '@web/settings/components/schedule/Breaks/useBreaksForm';

type Props = {
  fields: AutomatedBreakFormFields;
  automatedBreakType: AutomatedBreakType;
};

const AutomatedBreak = ({ fields, automatedBreakType }: Props) => {
  const isAttestationFeatureFlagEnabled = useIsAttestationsFeatureFlagEnabled();
  const { currentUser } = useAuth();
  const {
    hasError,
    updatePaid,
    updateBreakDuration,
    updateBreakThreshold,
    updateEarlyReturn,
    updatePremiumPay,
    isAttestationEnabled,
    errorText,
  } = useAutomatedBreakFields({ fields, automatedBreakType });
  const premiumPayFeatureFlagEnabled = useAutomatedBreaksPremiumPayFeatureFlagEnabled();

  return (
    <Column>
      <Row alignItems="center" justifyContent="space-between" mTop={20}>
        <Column>
          <Text14 semiBold themeColor={ThemeColorType.gray}>
            {gettext('Break category')}
          </Text14>
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext('Specify whether %(automatedBreakType)s breaks are paid or unpaid.', {
              automatedBreakType,
            })}
          </Text14>
        </Column>

        <Picker
          options={BREAK_CATEGORY_OPTIONS}
          value={fields.isPaid.value}
          customWidth={FIELD_WIDTH_205}
          onChange={updatePaid}
          disabled={!currentUser.isAdmin}
        />
      </Row>

      <Row alignItems="center" justifyContent="space-between" mTop={20}>
        <Column>
          <Text14 semiBold themeColor={ThemeColorType.gray}>
            {gettext('Automation rule')}
          </Text14>
          <AutomationRuleSummary
            breakDuration={fields.breakDuration}
            breakThreshold={fields.breakThreshold}
          />
          {hasError && (
            <Row mTop={5}>
              <Text11>&nbsp;</Text11>
            </Row>
          )}
        </Column>
        <Column
          alignItems={!fields.breakDuration.error && fields.breakThreshold.error ? 'end' : 'start'}>
          <Row alignItems="center">
            <CustomTextField
              width={FIELD_WIDTH_74}
              skipErrorMessage
              postfix={gettext('min')}
              maxLength={2}
              value={fields.breakDuration.value}
              onChange={updateBreakDuration}
              invalid={!!fields.breakDuration.error}
              onBlur={() => fields.breakDuration.runValidation()}
              disabled={!currentUser.isAdmin}
            />
            <Text12 themeColor={ThemeColorType.gray} pRight={10} pLeft={10} bold uppercase>
              {gettext('every')}
            </Text12>

            <CustomTextField
              width={FIELD_WIDTH_74}
              skipErrorMessage
              postfix={gettext('hrs')}
              value={fields.breakThreshold.value}
              onChange={updateBreakThreshold}
              invalid={!!fields.breakThreshold.error}
              onBlur={() => fields.breakThreshold.runValidation()}
              disabled={!currentUser.isAdmin}
            />
          </Row>
          {hasError && errorText !== CANNOT_BE_MORE_THAN_WAIVER_ERROR && (
            <Row mTop={5}>
              <Text11 themeColor={ThemeColorType.danger} bold>
                {errorText}
              </Text11>
            </Row>
          )}
        </Column>
      </Row>

      {isAttestationFeatureFlagEnabled && isAttestationEnabled ? (
        <WaivingSettings fields={fields} automatedBreakType={automatedBreakType} />
      ) : null}

      <SettingsSwitcherRow
        mTop={20}
        title={gettext('Early return')}
        subtitle={gettext('Enable early return from %(automatedBreakType)s breaks.', {
          automatedBreakType,
        })}
        value={fields.isEarlyReturn.value}
        onChange={updateEarlyReturn}
        disabled={!currentUser.isAdmin}
      />

      {premiumPayFeatureFlagEnabled && (
        <SettingsSwitcherRow
          mTop={20}
          title={gettext('Premium pay')}
          subtitle={gettext(
            'Add 1 extra hour of pay for %(automatedBreakType)s break violations.',
            {
              automatedBreakType,
            }
          )}
          value={fields.isPremiumPay.value}
          onChange={updatePremiumPay}
          disabled={!currentUser.isAdmin}
        />
      )}
    </Column>
  );
};

export default AutomatedBreak;
